const LANDING_DEMO_AUDIO_SIGNER_URL = 'https://europe-west1-students-data-base-e3ecf.cloudfunctions.net/getLandingDemoAudioUrl';
const LANDING_DEMO_ARTWORK_SIGNER_URL = 'https://europe-west1-students-data-base-e3ecf.cloudfunctions.net/getLandingDemoArtworkUrl';

const INTRO_EPISODE_CONFIG = {
  en: {
    searchIds: ['what-is-aprendos-en'],
    searchTitles: ['what is aprendos'],
    audioLocale: 'en',
    level: 'B1',
    title: 'The Impossible Survival',
    description: 'B1 English - Life & Culture',
    playerDescription: 'An extraordinary true story about survival, resilience, and one of the most impossible accidents in aviation history.',
    directAudioUrl: 'https://storage.googleapis.com/aprendos-audio-files/audio/demos/en/demo_en_landing.mp3',
    storageArtworkPath: 'demos/homepage/en/artwork-en.jpg',
  },
  es: {
    searchIds: ['what-is-aprendos-es'],
    searchTitles: ['que es aprendos', 'qué es aprendos'],
    audioLocale: 'es',
    level: 'B1',
    title: 'Besar es Cultura',
    description: 'B1 Español - Vida y Cultura',
    playerDescription: 'Una conversación sobre afecto y costumbres en distintas culturas.',
    directAudioUrl: 'https://storage.googleapis.com/aprendos-audio-files/audio/demos/es/demo_es_landing.mp3',
    storageArtworkPath: 'demos/homepage/es/artwork-es.jpg',
  },
  no: {
    searchIds: ['what-is-aprendos-no', 'what-is-aprendos-en'],
    searchTitles: ['hva er aprendos', 'what is aprendos'],
    audioLocale: 'no',
    level: 'B1',
    title: 'Hvorfor vi venter til siste minutt',
    description: 'Liv og kultur • B1',
    playerDescription: 'En gjenkjennelig samtale om utsettelse, følelser og hvorfor prokrastinering handler om mer enn bare latskap',
    directAudioUrl: 'https://storage.googleapis.com/aprendos-audio-files/audio/demos/no/demo_no_landing.mp3',
    storageArtworkPath: 'demos/homepage/no/artwork-no.jpg',
  },
};

function getConfig(uiLanguage) {
  const locale = normalizeIntroUiLocale(uiLanguage);
  return {
    locale,
    config: INTRO_EPISODE_CONFIG[locale] || INTRO_EPISODE_CONFIG.en,
  };
}

function mapEpisodeData([id, data], fallbackTitle) {
  return {
    id,
    title: data?.title || fallbackTitle,
    description: data?.description || data?.summary || data?.subtitle || '',
    playerDescription: data?.playerDescription || data?.player_description || data?.description || data?.summary || data?.subtitle || '',
    level: data?.level || 'B1',
    audioUrl: data?.audio_url || data?.audioUrl || `/audio/${id}.mp3`,
    artworkUrl: data?.artworkUrl || data?.artwork_url || data?.coverImage || null,
  };
}

function shouldUseSignedAudioUrl(rawUrl) {
  if (!rawUrl) return false;
  return rawUrl.includes('storage.googleapis.com/aprendos-audio-files/')
    || rawUrl.startsWith('gs://aprendos-audio-files/');
}

async function resolveConfiguredAudioUrl(config) {
  const rawUrl = config?.directAudioUrl || '';
  if (!rawUrl) return '';

  if (!shouldUseSignedAudioUrl(rawUrl)) {
    return rawUrl;
  }

  try {
    const signerUrl = `${LANDING_DEMO_AUDIO_SIGNER_URL}?lang=${encodeURIComponent(config.audioLocale || 'en')}`;
    const response = await fetch(signerUrl, { method: 'GET', mode: 'cors' });
    if (!response.ok) return '';

    const payload = await response.json();
    return typeof payload?.signedUrl === 'string' ? payload.signedUrl : '';
  } catch {
    return '';
  }
}

async function resolveConfiguredArtworkUrl(config) {
  const storagePath = config?.storageArtworkPath || '';
  if (!storagePath) return '';

  try {
    const signerUrl = `${LANDING_DEMO_ARTWORK_SIGNER_URL}?lang=${encodeURIComponent(config.audioLocale || 'en')}`;
    const response = await fetch(signerUrl, { method: 'GET', mode: 'cors' });
    if (!response.ok) return '';

    const payload = await response.json();
    return typeof payload?.signedUrl === 'string' ? payload.signedUrl : '';
  } catch {
    return '';
  }
}

export function normalizeIntroUiLocale(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized.startsWith('es')) return 'es';
  if (normalized.startsWith('no') || normalized.startsWith('nb') || normalized.startsWith('nn')) return 'no';
  return 'en';
}

export function findAprendosIntroEpisode(episodes, uiLanguage) {
  const { config } = getConfig(uiLanguage);

  return episodes.find(([id, data]) => {
    const normalizedId = String(id || '').toLowerCase();
    const normalizedTitle = String(data?.title || '').toLowerCase();
    return config.searchIds.some((candidate) => normalizedId.includes(candidate))
      || config.searchTitles.some((candidate) => normalizedTitle.includes(candidate));
  }) || null;
}

export function buildAprendosIntroFallback(uiLanguage, title) {
  const { config } = getConfig(uiLanguage);

  return {
    id: `what-is-aprendos-${config.audioLocale}`,
    title,
    description: '',
    playerDescription: '',
    level: config.level || 'B1',
    language: config.audioLocale,
    audioUrl: `/audio/what-is-aprendos-${config.audioLocale}.mp3`,
    artworkUrl: null,
  };
}

export async function resolveAprendosIntroEpisode(episodes, uiLanguage, title) {
  const { config } = getConfig(uiLanguage);
  const matchedEpisode = findAprendosIntroEpisode(episodes, uiLanguage);
  const baseEpisode = matchedEpisode
    ? mapEpisodeData(matchedEpisode, title)
    : buildAprendosIntroFallback(uiLanguage, title);

  const [configuredAudioUrl, configuredArtworkUrl] = await Promise.all([
    resolveConfiguredAudioUrl(config),
    resolveConfiguredArtworkUrl(config),
  ]);

  const useConfiguredPresentation = Boolean(configuredAudioUrl || configuredArtworkUrl);

  return {
    ...baseEpisode,
    title: useConfiguredPresentation ? (config.title || baseEpisode.title) : baseEpisode.title,
    description: useConfiguredPresentation ? (config.description || baseEpisode.description) : baseEpisode.description,
    playerDescription: useConfiguredPresentation
      ? (config.playerDescription || baseEpisode.playerDescription || baseEpisode.description)
      : (baseEpisode.playerDescription || baseEpisode.description),
    level: config.level || baseEpisode.level,
    language: config.audioLocale,
    audioUrl: configuredAudioUrl || baseEpisode.audioUrl,
    artworkUrl: configuredArtworkUrl || baseEpisode.artworkUrl,
  };
}
